<template>
  <button
    type="button"
    class="sso-button"
    :class="[
      `sso-button--${provider}`,
      {
        'sso-button--loading': loading,
        'sso-button--disabled': disabled && !loading,
      },
    ]"
    :disabled="disabled || loading"
    :aria-busy="loading || undefined"
    :aria-label="ariaLabel || label"
    @click="onClick"
  >
    <!-- Bouncing progress band (visible only while loading). Sits below the
         content layer; blurred + brand-gradient tinted for a soft, premium feel. -->
    <span v-if="loading" class="sso-button__progress" aria-hidden="true" />

    <!-- Content: icon + label. Stays visible during loading (the bouncing
         band animates behind it). -->
    <span class="sso-button__content">
      <span class="sso-button__icon" aria-hidden="true">
        <!-- Google -->
        <svg v-if="provider === 'google'" viewBox="0 0 24 24" width="20" height="20">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        <!-- Microsoft -->
        <svg v-else-if="provider === 'microsoft'" viewBox="0 0 23 23" width="20" height="20">
          <path fill="#f35325" d="M1 1h10v10H1z" />
          <path fill="#81bc06" d="M12 1h10v10H12z" />
          <path fill="#05a6f0" d="M1 12h10v10H1z" />
          <path fill="#ffba08" d="M12 12h10v10H12z" />
        </svg>
        <!-- Apple -->
        <svg v-else-if="provider === 'apple'" viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
        <!-- Keycloak (dev only) -->
        <svg v-else-if="provider === 'keycloak'" viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M12 1C8.676 1 6 3.676 6 7c0 1.628.648 3.103 1.697 4.188L3 16l1.5 1.5 1.5-1.5 1.5 1.5 1.5-1.5 1.5 1.5L12 16l-1.303-4.812C11.132 11.07 11.558 11 12 11c3.324 0 6-2.676 6-6s-2.676-6-6-6zm0 2c2.206 0 4 1.794 4 4s-1.794 4-4 4-4-1.794-4-4 1.794-4 4-4zm0 1.5c-1.381 0-2.5 1.119-2.5 2.5s1.119 2.5 2.5 2.5 2.5-1.119 2.5-2.5-1.119-2.5-2.5-2.5z" />
        </svg>
      </span>
      <span class="sso-button__label">{{ label }}</span>
    </span>
  </button>
</template>

<script setup lang="ts">
/**
 * SsoButton — Single sign-on button (Google / Apple / Microsoft / Keycloak).
 *
 * Presentational component. Parent owns the state — typically wired through
 * `useSocialLogin()` (UI app) so all SSO buttons + the email/password submit
 * button on a login form share one loading flag.
 *
 * Standard wiring pattern on a login page:
 *
 *   const { isLoading, currentProvider, loginWithProvider } = useSocialLogin();
 *   const isBusy = (p: SocialProvider) => isLoading.value && currentProvider.value === p;
 *   const otherBusy = (p: SocialProvider) => isLoading.value && currentProvider.value !== p;
 *
 *   <SsoButton
 *     v-for="p in providers"
 *     :provider="p"
 *     :label="t(`auth.continueWith.${p}`)"
 *     :loading="isBusy(p)"
 *     :disabled="otherBusy(p)"
 *     @click="loginWithProvider(p)"
 *   />
 *   <PrimaryButton type="submit" :loading="isLoading" :disable="isLoading" />
 *
 * Visual + motion language (2026):
 *   - 14px corner radius (M3 medium), 48px height (touch-friendly).
 *   - Subtle 1.5px border that lights up on hover.
 *   - Press scales to 0.97 for tactile feedback.
 *   - Focus-visible ring (a11y, never via `outline: none`).
 *   - Loading state DOES NOT swap the label for a spinner — the label stays
 *     in place and a soft, blurred brand-gradient band bounces left↔right
 *     behind it. Indeterminate progress without losing context.
 *   - Respects `prefers-reduced-motion` (the bounce drops to a static glow).
 */

interface Props {
  /** SSO provider — drives the icon. */
  provider: 'google' | 'microsoft' | 'apple' | 'keycloak';
  /** Full button label, already localized by the parent. */
  label: string;
  /** When true, shows the bouncing-band animation; the label stays visible. */
  loading?: boolean;
  /** When true, the button is non-interactive and visually faded. Use this
   *  for "another SSO button is busy — block this one" on the same form. */
  disabled?: boolean;
  /** Optional aria-label override. Defaults to `label`. */
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  disabled: false,
  ariaLabel: '',
});

const emit = defineEmits<{
  (e: 'click', provider: Props['provider']): void;
}>();

function onClick(): void {
  if (props.loading || props.disabled) return;
  emit('click', props.provider);
}
</script>

<style scoped lang="scss">
.sso-button {
  --sso-radius: 14px;
  --sso-height: 48px;
  --sso-bg: var(--card-bg, #fff);
  --sso-bg-hover: var(--input-bg, #f7f8fa);
  --sso-border: var(--card-border, #e3e5ea);
  --sso-border-hover: color-mix(in srgb, var(--q-primary, #1A4D8F) 35%, var(--card-border, #e3e5ea));
  --sso-color: var(--text-color-secondary, #4B4F55);
  --sso-color-hover: var(--text-color, #1E1E1E);
  --sso-focus: var(--q-primary, #1A4D8F);
  --sso-gradient-start: var(--brand-gradient-start, #1A4D8F);
  --sso-gradient-end: var(--brand-gradient-end, #1F9DA0);

  position: relative;
  isolation: isolate; /* Establishes a fresh stacking context so .sso-button__progress (z-index 0) stays under .sso-button__content (z-index 1) even if the parent is itself positioned. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: var(--sso-height);
  padding: 0 22px;
  background: var(--sso-bg);
  color: var(--sso-color);
  border: 1.5px solid var(--sso-border);
  border-radius: var(--sso-radius);
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  transition:
    background-color 180ms ease,
    border-color 180ms ease,
    color 180ms ease,
    box-shadow 180ms ease,
    transform 100ms ease;

  &:hover:not(:disabled) {
    background: var(--sso-bg-hover);
    border-color: var(--sso-border-hover);
    color: var(--sso-color-hover);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }

  &:focus-visible {
    outline: 2px solid var(--sso-focus);
    outline-offset: 3px;
  }

  &:disabled {
    cursor: not-allowed;
  }

  /* Disabled-but-not-loading: greyed out, no animation. */
  &.sso-button--disabled {
    opacity: 0.42;
    filter: saturate(0.6);
  }

  /* Loading: keep at full opacity (the bouncing band tells the user something
     is happening); disable the hover/press affordances. */
  &.sso-button--loading {
    cursor: progress;
    pointer-events: none;
    border-color: color-mix(in srgb, var(--sso-gradient-start) 25%, var(--sso-border));
  }
}

.sso-button__content {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.sso-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.sso-button__label {
  white-space: nowrap;
}

/* Bouncing progress band — uses the brand gradient with low alpha, blurred
   for that soft "neon glow under glass" feel. Travels left → right → left
   with ease-in-out so the speed peaks in the middle and slows at the edges
   (true "bounce" feel, not constant velocity). */
.sso-button__progress {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -50%;
  width: 50%;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    transparent 0%,
    color-mix(in srgb, var(--sso-gradient-start) 30%, transparent) 30%,
    color-mix(in srgb, var(--sso-gradient-end) 38%, transparent) 50%,
    color-mix(in srgb, var(--sso-gradient-start) 30%, transparent) 70%,
    transparent 100%
  );
  filter: blur(10px);
  animation: sso-button-bounce 1.8s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;
  will-change: transform;
}

@keyframes sso-button-bounce {
  0%   { transform: translateX(0); }
  50%  { transform: translateX(300%); }
  100% { transform: translateX(0); }
}

@media (prefers-reduced-motion: reduce) {
  .sso-button__progress {
    animation: none;
    left: 25%;
    width: 50%;
    opacity: 0.6;
    filter: blur(14px);
  }

  .sso-button:active:not(:disabled) {
    transform: none;
  }
}
</style>
